import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { ClipMedia, Moment, Shape, VoiceNote } from "@coaching/types";
import { ANNOTATION_COLOURS, DEFAULT_HOLD_SEC, DEFAULT_STROKE, momentAt, reviewHasContent } from "@coaching/annotations";
import { api, ApiError, watchSubmission, type Submission } from "../api";
import { Centred, ErrorState } from "../components/States";
import { DeclineDialog } from "../components/DeclineDialog";
import { FocusChips } from "../components/FocusChips";
import { SmallScreenNote } from "../components/SmallScreenNote";
import { STATUS_LABEL, bucketLength, relative } from "../format";
import { useMedia } from "../review/useMedia";
import { useDraft, type SaveState } from "../review/useDraft";
import { Toolbar, type ToolState } from "../review/Toolbar";
import { VideoStage, type StageHandle } from "../review/VideoStage";
import { ClipStrip } from "../review/Timeline";
import { MomentEditor, MomentList } from "../review/MomentPanel";
import { VoiceRecorder } from "../review/VoiceRecorder";
import { useCoachingConfig } from "../components/PromiseNote";
import { useImprove } from "../review/Improve";

const NEAR_SEC = 0.5;
const newId = () => (crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "").slice(0, 20) : `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);

export default function Review() {
    const { id = "" } = useParams();
    const navigate = useNavigate();
    const [submission, setSubmission] = useState<Submission | null | undefined>(undefined);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        setLoadError(null);
        return watchSubmission(id, setSubmission, (e) => setLoadError(e.message));
    }, [id, attempt]);

    if (loadError) return <ErrorState title="Could not open this submission" body={loadError} retry={() => setAttempt((n) => n + 1)} />;
    if (submission === undefined) return <Centred spinner>Opening the review</Centred>;
    if (submission === null) return <ErrorState title="This submission no longer exists" retry={() => navigate("/")} />;
    return <Workspace submission={submission} />;
}

/** A drawing without its time in a voice note, for when the note is recorded again. */
function untimed(s: Shape): Shape {
    const copy = { ...s };
    delete copy.at;
    return copy;
}

/** Where the playhead is: which clip, and the time to land on when it loads. */
interface Position {
    clipIndex: number;
    startAt: number;
    autoplay: boolean;
}

function Workspace({ submission: s }: { submission: Submission }) {
    const navigate = useNavigate();
    const editable = s.status === "accepted" || s.status === "in_review";
    const purged = !!s.timing.purgeAt && s.timing.purgeAt.toMillis() <= Date.now();
    const clipsReady = s.cut.status === "ready" && !purged;
    const media = useMedia(s.id, clipsReady);
    const clips: ClipMedia[] = useMemo(() => (media.state.kind === "ready" ? media.state.media.clips : []), [media.state]);
    const clipIds = useMemo(() => clips.map((c) => c.clipId), [clips]);
    const { draft, save, setSummary, setSummaryVoice, updateMoments, saveNow, reload } = useDraft(s, editable);
    const cfg = useCoachingConfig();
    const maxVoiceSec = cfg?.maxVoiceSec ?? 120;
    const stage = useRef<StageHandle>(null);
    const [position, setPosition] = useState<Position>({ clipIndex: 0, startAt: 0, autoplay: false });
    const [time, setTime] = useState(0);
    const [paused, setPaused] = useState(true);
    const [tool, setTool] = useState<ToolState>({ kind: "path", color: ANNOTATION_COLOURS[0], width: DEFAULT_STROKE });
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [declining, setDeclining] = useState(false);
    const [returning, setReturning] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState<"accept" | "decline" | "return" | null>(null);
    const busy = busyAction !== null;
    // Shapes undone on a moment, newest last, so redo can bring them back until something new is drawn.
    const [redo, setRedo] = useState<{ momentId: string; shapes: Shape[] } | null>(null);
    const mediaErrorHandled = useRef(0);

    const clip = clips[Math.min(position.clipIndex, Math.max(0, clips.length - 1))] ?? null;
    const clipId = clip?.clipId ?? null;

    const onTime = useCallback((t: number, p: boolean) => { setTime(t); setPaused(p); }, []);

    // The moment being edited: the one at the playhead in this clip while paused, or an explicit pick.
    const activeMoment = useMemo(() => {
        if (!paused || !clipId) return null;
        const picked = selectedId ? draft.moments.find((m) => m.id === selectedId) : undefined;
        if (picked && picked.clipId === clipId && Math.abs(picked.t - time) <= NEAR_SEC) return picked;
        let best: Moment | null = null;
        for (const m of draft.moments) {
            if (m.clipId !== clipId) continue;
            if (Math.abs(m.t - time) <= NEAR_SEC && (!best || Math.abs(m.t - time) < Math.abs(best.t - time))) best = m;
        }
        return best;
    }, [paused, selectedId, draft.moments, time, clipId]);

    const playingMoment = useMemo(() => (paused || !clipId ? null : momentAt(draft.moments, clipId, time)), [paused, draft.moments, time, clipId]);
    const shownShapes: Shape[] = (activeMoment ?? playingMoment)?.shapes ?? [];
    const canDraw = editable && clipsReady && paused && !!clip && (save.kind !== "conflict" && save.kind !== "locked");

    const sortMoments = useCallback((ms: Moment[]) => {
        const index = (m: Moment) => { const i = clipIds.indexOf(m.clipId); return i < 0 ? clipIds.length : i; };
        return [...ms].sort((a, b) => index(a) - index(b) || a.t - b.t).map((x, i) => ({ ...x, order: i }));
    }, [clipIds]);

    const addMoment = useCallback((): Moment | null => {
        if (!clipId) return null;
        const t = stage.current?.frameTime() ?? time;
        const m: Moment = { id: newId(), clipId, t: Math.round(t * 1000) / 1000, holdSec: DEFAULT_HOLD_SEC, label: "", note: "", shapes: [], order: 0 };
        updateMoments((ms) => sortMoments([...ms, m]));
        setSelectedId(m.id);
        return m;
    }, [clipId, time, updateMoments, sortMoments]);

    // While a voice note is recording on a moment, what is drawn on it carries its time into the note.
    const recording = useRef<{ momentId: string; startedAt: number | null } | null>(null);

    const onShape = useCallback((shape: Shape) => {
        const target = activeMoment ?? addMoment();
        if (!target) return;
        setRedo(null);
        const rec = recording.current;
        const timed: Shape = rec && rec.momentId === target.id && rec.startedAt !== null
            ? { ...shape, at: Math.round(((Date.now() - rec.startedAt) / 1000) * 100) / 100 }
            : shape;
        updateMoments((ms) => ms.map((m) => (m.id === target.id ? { ...m, shapes: [...m.shapes, timed] } : m)));
    }, [activeMoment, addMoment, updateMoments]);

    /** Talk & draw begins: on the moment at this frame, made if there is none; a new take forgets the old drawing times. */
    const startVoice = () => {
        stage.current?.pause();
        const target = activeMoment ?? addMoment();
        if (!target) return;
        recording.current = { momentId: target.id, startedAt: null };
        updateMoments((ms) => ms.map((m) => (m.id === target.id ? { ...m, shapes: m.shapes.map(untimed) } : m)));
    };
    const [voiceLive, setVoiceLive] = useState(false);
    const onRecording = (startedAt: number | null) => {
        setVoiceLive(startedAt !== null);
        if (startedAt !== null && recording.current) recording.current.startedAt = startedAt;
    };
    const voiceSaved = (v: VoiceNote | null) => {
        const id = recording.current?.momentId ?? activeMoment?.id;
        recording.current = null;
        if (id) patchMoment(id, { voice: v });
    };

    const patchMoment = (mid: string, patch: Partial<Moment>) => updateMoments((ms) => ms.map((m) => (m.id === mid ? { ...m, ...patch } : m)));
    const deleteMoment = (mid: string) => { updateMoments((ms) => sortMoments(ms.filter((m) => m.id !== mid))); setSelectedId(null); };
    const undo = () => {
        if (!activeMoment || activeMoment.shapes.length === 0) return;
        const last = activeMoment.shapes[activeMoment.shapes.length - 1];
        setRedo((r) => ({ momentId: activeMoment.id, shapes: [...(r?.momentId === activeMoment.id ? r.shapes : []), last] }));
        patchMoment(activeMoment.id, { shapes: activeMoment.shapes.slice(0, -1) });
    };
    const redoShape = () => {
        if (!activeMoment || redo?.momentId !== activeMoment.id || redo.shapes.length === 0) return;
        const back = redo.shapes[redo.shapes.length - 1];
        setRedo({ momentId: redo.momentId, shapes: redo.shapes.slice(0, -1) });
        patchMoment(activeMoment.id, { shapes: [...activeMoment.shapes, back] });
    };
    const canRedo = !!activeMoment && redo?.momentId === activeMoment.id && redo.shapes.length > 0;
    const clear = () => { if (activeMoment) { setRedo(null); patchMoment(activeMoment.id, { shapes: [] }); } };

    /** Land on a time in a clip, switching clip when it is not the one playing. */
    const seekTo = useCallback((clipIndex: number, t: number, autoplay = false) => {
        if (clipIndex === position.clipIndex && clips[clipIndex]) {
            if (!autoplay) stage.current?.pause();
            stage.current?.seek(t);
            return;
        }
        setPosition({ clipIndex, startAt: t, autoplay });
        setTime(t);
        setPaused(!autoplay);
    }, [position.clipIndex, clips]);

    const select = (m: Moment) => {
        setSelectedId(m.id);
        const i = clipIds.indexOf(m.clipId);
        if (i >= 0) seekTo(i, m.t);
    };
    const mark = () => { stage.current?.pause(); if (!activeMoment) addMoment(); };
    const onEnded = useCallback(() => {
        if (position.clipIndex + 1 < clips.length) seekTo(position.clipIndex + 1, 0, true);
    }, [position.clipIndex, clips.length, seekTo]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const el = e.target as HTMLElement | null;
            if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) {
                if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void saveNow(); }
                return;
            }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); void saveNow(); return; }
            if ((e.metaKey || e.ctrlKey) && ((e.shiftKey && e.key.toLowerCase() === "z") || e.key.toLowerCase() === "y")) { e.preventDefault(); redoShape(); return; }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") { e.preventDefault(); undo(); return; }
            const tools: Record<string, ToolState["kind"]> = { p: "path", l: "line", a: "arrow", c: "circle", b: "rect" };
            const k = e.key.toLowerCase();
            if (k === " ") { e.preventDefault(); stage.current?.togglePlay(); }
            else if (k === "[") stage.current?.nudge(-1);
            else if (k === "]") stage.current?.nudge(1);
            else if (k === ",") stage.current?.step(-1);
            else if (k === ".") stage.current?.step(1);
            else if (k === "m") mark();
            else if (tools[k]) setTool((t) => ({ ...t, kind: tools[k] }));
            else if (/^[1-5]$/.test(k)) setTool((t) => ({ ...t, color: ANNOTATION_COLOURS[Number(k) - 1] }));
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    const respond = async (action: "accept" | "decline", message?: string) => {
        setBusyAction(action); setActionError(null);
        try { await api.respond(s.id, action, message); setDeclining(false); } catch (err) {
            setActionError(err instanceof ApiError ? err.message : "That did not go through. Check your connection and try again.");
        } finally { setBusyAction(null); }
    };

    const doReturn = async () => {
        setBusyAction("return"); setActionError(null);
        try {
            await saveNow();
            await api.returnReview(s.id);
            setReturning(false);
            navigate("/");
        } catch (err) {
            setActionError(err instanceof ApiError ? err.message : "Could not return the review. Check your connection and try again.");
        } finally { setBusyAction(null); }
    };

    const onMediaError = useCallback(() => {
        // One refresh per expiry: an expired signature is the usual cause; anything else is surfaced by useMedia.
        if (Date.now() - mediaErrorHandled.current < 10_000) return;
        mediaErrorHandled.current = Date.now();
        void media.refresh();
    }, [media]);

    // Dev builds expose the stage so the emulator drive can seek to exact times.
    useEffect(() => {
        if (import.meta.env.DEV) (window as unknown as { __coachStage?: StageHandle | null }).__coachStage = stage.current;
    });

    const hasContent = reviewHasContent(draft.summary, draft.moments, draft.summaryVoice);
    const voiceBase = media.state.kind === "ready" ? media.state.media.voice ?? null : null;
    const voiceUrl = (key: string) => (voiceBase ? `${voiceBase.baseUrl}${key}?${voiceBase.query}` : null);
    const uploadVoice = (audio: string): Promise<VoiceNote> => api.uploadVoice(s.id, audio);
    const improveOn = (cfg?.maxImprovesPerDay ?? 0) > 0;
    const improveNote = async (text: string, momentTitle: string) => (await api.improve(s.id, "note", text, momentTitle)).text;
    const summaryAssist = useImprove(draft.summary, async (t) => (await api.improve(s.id, "summary", t)).text, setSummary);
    const deadline = s.status === "submitted" ? `Answer ${relative(s.timing.respondByAt)}` : editable ? `Due ${relative(s.timing.dueAt)}` : "";

    return (
        <div className="review">
            <header className="review-head">
                <Link to="/inbox" className="back">← Inbox</Link>
                <div className="review-title">
                    <strong>{s.playerDisplayName}</strong>
                    <span className="muted small">{s.clubName} · {bucketLength(s.clipCount, s.totalSec)} · {deadline}</span>
                </div>
                <span className={`pill ${s.status}`}>{STATUS_LABEL[s.status]}</span>
                <div className="review-actions">
                    {s.status === "submitted" && (
                        <>
                            <button className="btn ok" disabled={busy || s.cut.status === "failed"} onClick={() => void respond("accept")} aria-busy={busyAction === "accept"}>
                                {busyAction === "accept" ? <><span className="spinner small on-light" aria-hidden /> Accepting</> : "Accept"}
                            </button>
                            <button className="btn decline" disabled={busy} onClick={() => setDeclining(true)}>Decline</button>
                        </>
                    )}
                    {editable && <SaveBadge save={save} onRetry={() => void saveNow()} />}
                    {editable && <button className="btn primary" disabled={busy || !hasContent || save.kind === "conflict" || save.kind === "locked"} onClick={() => setReturning(true)}>Return to player</button>}
                </div>
            </header>
            <SmallScreenNote />
            <FocusChips areas={s.focusAreas} prefix="Asked about" />
            {s.note && <p className="note review-note">Player asked: “{s.note}”</p>}
            {actionError && <p className="error-text" role="alert">{actionError}</p>}

            {save.kind === "conflict" && (
                <div className="banner warn" role="alert">
                    <strong>This review was changed in another tab or on another device.</strong>
                    <span> Reload to pick up the latest version. Anything you drew since your last save here will be lost, so copy any notes you need first.</span>
                    <button className="btn small" onClick={() => void reload()}>Reload latest</button>
                </div>
            )}
            {save.kind === "locked" && <div className="banner warn" role="alert">{save.message}</div>}
            {save.kind === "load_error" && <div className="banner warn" role="alert">Could not load the saved review. <button className="btn small" onClick={() => void reload()}>Try again</button></div>}

            <div className="studio">
                <div className="studio-main">
                    <section className="stage-wrap">
                        {purged && <ErrorState title="These clips have been removed" body="Clips are kept for a limited time after a review closes." />}
                        {!purged && s.cut.status === "pending" && <Centred spinner>Preparing the clips, {s.cut.readyCount} of {s.clipCount} ready. This usually takes a minute or two.</Centred>}
                        {!purged && s.cut.status === "failed" && <ErrorState title="These clips could not be prepared" body="The player has been told and can send the footage again." />}
                        {clipsReady && media.state.kind === "loading" && <Centred spinner>Loading the clips</Centred>}
                        {clipsReady && media.state.kind === "error" && <ErrorState title="Could not load the clips" body={media.state.message} retry={media.state.retryable ? () => void media.retry() : undefined} />}
                        {clipsReady && media.state.kind === "ready" && clip && (
                            <VideoStage ref={stage} src={clip.playlistUrl} clipKey={clip.clipId} startAt={position.startAt} autoplay={position.autoplay}
                                generation={media.state.generation}
                                frameWidth={clip.width} frameHeight={clip.height}
                                shapes={shownShapes} tool={tool} canDraw={canDraw}
                                onTime={onTime} onDurationKnown={() => undefined} onEnded={onEnded} onShape={onShape} onMediaError={onMediaError} />
                        )}
                        {canDraw && <span className="stage-badge">Drawing on this frame</span>}
                    </section>

                    {clipsReady && media.state.kind === "ready" && clip && (
                        <>
                            <ClipStrip clips={clips} clipIndex={position.clipIndex} time={time} paused={paused} moments={draft.moments} activeId={activeMoment?.id ?? null}
                                onSeek={(i, t) => seekTo(i, t)} onTogglePlay={() => stage.current?.togglePlay()}
                                onStep={(frames) => stage.current?.step(frames)} onNudge={(sec) => stage.current?.nudge(sec)}
                                onSelect={select} onMark={mark} canMark={editable && paused && !activeMoment} />
                            {editable && <Toolbar tool={tool} onChange={setTool} onUndo={undo} onRedo={redoShape} onClear={clear}
                                canUndo={!!activeMoment && activeMoment.shapes.length > 0} canRedo={canRedo}
                                canClear={!!activeMoment && activeMoment.shapes.length > 0} disabled={!canDraw}
                                voice={canDraw || voiceLive ? (
                                    <VoiceRecorder compact recordLabel="Talk & draw" voice={activeMoment?.voice ?? null}
                                        url={activeMoment?.voice ? voiceUrl(activeMoment.voice.key) : null} maxSec={maxVoiceSec}
                                        disabled={!canDraw} upload={uploadVoice} onStart={startVoice} onRecording={onRecording} onChange={voiceSaved} />
                                ) : undefined} />}
                            {!paused && editable && <p className="muted small hint">Pause to draw. Drawing on a paused frame makes a moment at that time.</p>}
                        </>
                    )}
                </div>

                <aside className="studio-side">
                    <section className="card panel">
                        <div className="panel-head"><h2>Moments</h2><span className="count">{draft.moments.length}</span></div>
                        <MomentList moments={draft.moments} clipIds={clipIds} activeId={activeMoment?.id ?? null} onSelect={select} />
                    </section>
                    <section className="card panel">
                        {activeMoment ? (
                            <MomentEditor moment={activeMoment} clipIds={clipIds} editable={editable && save.kind !== "conflict"}
                                improve={improveOn ? improveNote : undefined}
                                onChange={(patch) => patchMoment(activeMoment.id, patch)} onDelete={() => deleteMoment(activeMoment.id)} />
                        ) : (
                            <div className="editor-empty">
                                <p><strong>{editable ? "Pause on a frame to start a moment" : "Select a moment"}</strong></p>
                                <p className="muted small">{editable ? "Draw on it, give it a title and a note, and talk the player through it." : "Its title, note and voice note show here."}</p>
                            </div>
                        )}
                    </section>
                    <section className="card panel">
                        <div className="panel-head"><h2>Summary</h2>{improveOn && editable && summaryAssist.button(save.kind === "conflict")}</div>
                        <textarea className="summary" rows={6} maxLength={4000} value={draft.summary} disabled={!editable || save.kind === "conflict"}
                            placeholder="The overall picture: what is working, the one or two things to change, and what to practise next."
                            onChange={(e) => setSummary(e.target.value)} />
                        {summaryAssist.panel}
                        <VoiceRecorder label="Spoken summary" voice={draft.summaryVoice} url={draft.summaryVoice ? voiceUrl(draft.summaryVoice.key) : null}
                            maxSec={maxVoiceSec} disabled={!editable || save.kind === "conflict"} upload={uploadVoice} onChange={setSummaryVoice} />
                    </section>
                </aside>
            </div>

            {declining && <DeclineDialog playerName={s.playerDisplayName} busy={busy} onCancel={() => setDeclining(false)} onConfirm={(m) => void respond("decline", m)} />}
            {returning && (
                <div className="modal-backdrop" onClick={busy ? undefined : () => setReturning(false)}>
                    <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
                        <h2>Return this review to {s.playerDisplayName}?</h2>
                        <p className="muted">{draft.moments.length} {draft.moments.length === 1 ? "moment" : "moments"}{draft.moments.some((m) => m.voice) ? " with voice notes" : ""}{draft.summary.trim() || draft.summaryVoice ? " and a summary" : ", no summary"}. They will get a notification and the review cannot be edited afterwards.</p>
                        <div className="modal-actions">
                            <button className="btn" onClick={() => setReturning(false)} disabled={busy}>Keep editing</button>
                            <button className="btn primary" onClick={() => void doReturn()} disabled={busy}>{busyAction === "return" ? <><span className="spinner small on-light" aria-hidden /> Returning</> : "Return review"}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function SaveBadge({ save, onRetry }: { save: SaveState; onRetry: () => void }) {
    switch (save.kind) {
    case "loading": return <span className="save muted small">Loading</span>;
    case "clean": return <span className="save muted small">Up to date</span>;
    case "dirty": return <span className="save muted small">Unsaved changes</span>;
    case "saving": return <span className="save muted small">Saving</span>;
    case "saved": return <span className="save ok-text small">Saved</span>;
    case "error": return <button className="save btn ghost small warn-text" onClick={onRetry}>Could not save, tap to retry</button>;
    case "conflict": return <span className="save warn-text small">Changed elsewhere</span>;
    case "locked": return <span className="save warn-text small">Read only</span>;
    case "load_error": return <span className="save warn-text small">Not loaded</span>;
    }
}
